import { dayEntries, isExtraSlot } from './skeletalLayout';

// ── Skeletal → full itinerary ───────────────────────────────────────────────
// Turns an approved skeletal into a starting point for the full itinerary.
// The skeletal only knows "Morning", but the full itinerary needs a start time
// and sorts each day by it, so every activity gets a suggested time that keeps
// the skeletal's order. Staff replace it when they fill in the details.

const SLOT_TIMES = {
  arrival: '07:00', breakfast: '08:00', morning1: '10:00', morning2: '11:30', lunch: '13:00',
  afternoon1: '15:00', afternoon2: '16:30', evening1: '18:30', evening2: '19:15', evmeal: '20:00', departure: '21:00',
};

const MIN_GAP = 30;
const EXTRA_GAP = 60;
const LAST_TIME = 23 * 60 + 59;

// Plain meals become the same items choosing a hotel creates. A custom label
// ("Lunch at the stadium") stays an ordinary activity so its wording survives.
const MEAL_TITLES = ['Breakfast', 'Lunch', 'Evening Meal'];

function toMinutes(time) {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function toTime(minutes) {
  const clamped = Math.min(minutes, LAST_TIME);
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

// Labels pasted into the skeletal can carry invisible characters.
function cleanLabel(label) {
  return String(label || '').replace(/[​-‍⁠﻿]/g, '').trim();
}

function mealTitle(cell, label) {
  if (cell.type !== 'meal') return null;
  return MEAL_TITLES.find((title) => title.toLowerCase() === label.toLowerCase()) || null;
}

/**
 * The activities a skeletal gives, in order:
 * [{ day (1-based), time, title, isMeal }] — skipping hidden, empty and
 * unfilled placeholder slots. Moved lunches and extra slots keep their place
 * because each time is pushed past the one before it.
 */
export function skeletalToItems(skeletal, nDays) {
  const items = [];
  for (let d = 0; d < nDays; d++) {
    let previous = null;
    dayEntries(skeletal || {}, d).forEach(({ key, cell }) => {
      const label = cleanLabel(cell.label);
      if (!label || cell.type === 'slot') return;
      const base = isExtraSlot(key) || !SLOT_TIMES[key] ? null : toMinutes(SLOT_TIMES[key]);
      let minutes = base ?? (previous === null ? toMinutes(SLOT_TIMES.morning1) : previous + EXTRA_GAP);
      if (previous !== null) minutes = Math.max(minutes, previous + MIN_GAP);
      previous = minutes;
      const meal = mealTitle(cell, label);
      items.push({ day: d + 1, time: toTime(minutes), title: meal || label, isMeal: !!meal });
    });
  }
  return items;
}

/** Drops meals already on the itinerary, e.g. those created by choosing a hotel. */
export function withoutExistingMeals(skeletalItems, existing) {
  return skeletalItems.filter((item) => !item.isMeal || !existing.some((e) => (
    e.itemType === 'meal' && e.day === item.day && e.title === item.title
  )));
}
